import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Link } from 'react-router'

import { useTranslation } from 'react-i18next'

import {
  useFetchLastSubscriptionsQuery,
} from 'api/dashboard'

import { userTimeZoneFormat } from 'utils/time'
import { RouteTicketingSystemsTicket } from 'routes'
import { SUBSCRIPTION_STATUS_COLOR_MAP } from 'constants/colorMaps'
import Table from 'components/Shared/TableNew/Index'
import TableHead from 'components/Shared/TableNew/TableHead'
import TableBody from 'components/Shared/TableNew/TableBody'
import TableRow from 'components/Shared/TableNew/TableRow'
import TableRowNoData from 'components/Shared/TableNew/TableRowNoData'
import TableCell from 'components/Shared/TableNew/TableCell'
import colors from 'components/utils/colors'
import Badge from 'components/Shared/Badge/Index'
import LoadingIndicator from 'components/Shared/LoadingIndicator/Index'
import Tooltip from 'components/Shared/Tooltip/Index'
import IconLink from 'components/Shared/IconLink/Index'
import LegendBar from 'components/Shared/LegendBar/Index'

const StyledWrapper = styled.div`
  position: relative;
  display: flex;
  min-width: 400px;
  min-height: 325px;
  flex-direction: column;
  padding: 10px 16px 10px 16px;
  background-color: #FFFFFF;
  flex: 1;
  box-sizing: border-box;
  border-radius: 3px;
`

const Title = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: Exo;
  font-size: 14px;
  font-weight: 500;
  color: ${colors.secondary20};
  margin-bottom: 15px;
`

const StyledLegendBar = styled(LegendBar)`
  align-self: flex-start;
  margin-top: 15px;
`

const StyledLink = styled(Link)`
  display: inline-block;

  &:after {
    content: ',';
    margin-right: 5px;
  }

  &:last-of-type:after {
    content: none;
  }
`

const LastSubscriptionsTable = ({ ticketingSystemDisplayId, userTimeZone }) => {
  const { t } = useTranslation()

  const { data, isFetching } =
    useFetchLastSubscriptionsQuery({
      ticketingSystemDisplayId: ticketingSystemDisplayId,
    })

  const subscriptionsList = data?.lastSubscriptions || []

  const tooltipText = t('dashboardPage.activeSubscTooltip')
  const tooltipId = 'subscription-tooltip'
  const isEmpty = subscriptionsList.length === 0

  const legendItems = [
    { color: SUBSCRIPTION_STATUS_COLOR_MAP['success'], label: t('common.success') },
    { color: SUBSCRIPTION_STATUS_COLOR_MAP['failed'], label: t('common.failed') },
  ]

  return (
    <StyledWrapper data-test='last-subscriptions'>
      <LoadingIndicator loading={isFetching} small />
      <Title>
        {t('dashboardPage.activeSubscHeader')}
        <IconLink
          data-tooltip-content={tooltipText}
          data-tooltip-id={tooltipId}
          iconName='icon-help-16'
        />
        <Tooltip id='subscription-tooltip' />
      </Title>

      <Table width='100%'>
        <TableHead>
          <TableRow>
            <TableCell>{t('dashboardPage.user')}</TableCell>
            <TableCell>{t('dashboardPage.time')}</TableCell>
            <TableCell>{t('dashboardPage.status')}</TableCell>
            <TableCell>{t('dashboardPage.ticket')}</TableCell>
          </TableRow>
        </TableHead>

        <TableBody clickable={false} hoverable={false}>
          {subscriptionsList.map(subscription => {
            const time = userTimeZoneFormat(userTimeZone, subscription.sentAt)
            const statusColor = SUBSCRIPTION_STATUS_COLOR_MAP[subscription.state]

            return (
              <TableRow key={subscription.id}>
                <TableCell>
                  {subscription.userName}
                </TableCell>
                <TableCell>
                  {time}
                </TableCell>
                <TableCell>
                  <Badge big color={statusColor} margin='0px'>
                    {subscription.state}
                  </Badge>
                </TableCell>
                <TableCell>
                  {subscription.ticketsMetaData.map(ticketExternalId => {
                    const ticketUrl = RouteTicketingSystemsTicket({
                      ticketingSystemDisplayId: subscription.ticketingSystemDisplayId,
                      ticketId: ticketExternalId,
                    })

                    return (
                      <StyledLink key={ticketExternalId} to={ticketUrl}>{ticketExternalId}</StyledLink>
                    )
                  })}
                </TableCell>
              </TableRow>
            )
          })}
          <TableRowNoData data={subscriptionsList} />
        </TableBody>
      </Table>
      {!isEmpty && <StyledLegendBar items={legendItems} />}
    </StyledWrapper>
  )
}

LastSubscriptionsTable.propTypes = {
  ticketingSystemDisplayId: PropTypes.string,
  userTimeZone: PropTypes.string,
}

export default LastSubscriptionsTable
